/**
 * Authentication Page
 *
 * Single sign-in button that redirects to Cognito Hosted UI.
 * Roles are determined from Cognito groups after successful login.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import lotusLogo from "@/assets/lotus-bakeries-logo.png";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/hooks/useAuth";
import { authLogger } from "@/lib/logger";
import LoadingSpinner from "@/components/LoadingSpinner";
import { LogIn, AlertCircle } from "lucide-react";

const Auth = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, userRole, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const destination = userRole === 'admin' ? "/admin/projects" : "/tester/dashboard";
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, isLoading, userRole, navigate]);

  const handleSignIn = useCallback(async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await login();
      // Will redirect to Cognito — no further action needed
    } catch (err) {
      authLogger.error('Sign in failed:', err);
      setError(err instanceof Error ? err.message : 'Sign in failed. Please try again.');
      setIsSubmitting(false);
    }
  }, [login]);

  // =============================================================================
  // Loading State
  // =============================================================================

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4" role="status" aria-label="Loading">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="w-full py-12 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-center">
          <img
            src={lotusLogo}
            alt="Lotus Bakeries"
            className="h-10 md:h-12 object-contain"
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md animate-fade-in">
          {/* Title Section */}
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-display font-medium text-foreground mb-2">
              Testing Portal
            </h1>
            <p className="text-muted-foreground/80 text-sm">
              Sign in with your organization account
            </p>
          </div>

          <Card className="shadow-sm border-border/50 rounded-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-lg font-display font-medium">
                Welcome back
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground/70">
                Use your organization credentials
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-2">
              {/* Error Alert */}
              {error && (
                <Alert
                  variant="destructive"
                  role="alert"
                  className="mb-4"
                >
                  <AlertCircle className="h-4 w-4" aria-hidden="true" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Sign in with SSO */}
              <Button
                size="lg"
                variant="lotus"
                onClick={handleSignIn}
                disabled={isSubmitting}
                className="w-full transition-all duration-200"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <LoadingSpinner size="sm" className="text-current" />
                    Redirecting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="h-4 w-4" aria-hidden="true" />
                    Sign in with SSO
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full py-8 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs text-muted-foreground/60">
            © {new Date().getFullYear()} Lotus Bakeries
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Auth;
